<template>
  <div class="container-fluid login-background">
    <div class="row">
      <div class="col-lg-6">
        <div class="block-card login-wrapper d-flex flex-column position-relative">
          <!-- 新增：返回首頁按鈕 -->
          <router-link to="/" class="back-to-home-btn" :aria-label="$t('backToHome')">
            <i class="bi bi-x-lg"></i>
          </router-link>
          
          <h1 class="login-title">{{ $t("welcome") }}<br>{{ $t("platform") }}</h1>
          
          <form @submit.prevent="onSubmit">
            <div class="mb-3">
              <label class="form-label" for="login-username">{{ $t("account") }}</label>
              <input
                id="login-username"
                v-model="username"
                type="text"
                class="form-control custom-input"
                :placeholder="$t('enterAccount')"
                autocomplete="username"
              >
            </div>

            <div class="mb-3">
              <label class="form-label" for="login-password">{{ $t("passwordLabel") }}</label>
              <input
                id="login-password"
                v-model="password"
                type="password"
                class="form-control custom-input"
                :placeholder="$t('enterPassword')"
                autocomplete="current-password"
              >
            </div>
            
            <!-- 錯誤訊息 -->
            <div v-if="errorMessage" class="alert alert-danger custom-alert">
              {{ errorMessage }}
            </div>

            <!-- 登入按鈕 - 修改為垂直排列 -->
            <button 
              type="submit" 
              class="btn block-btn login-btn" 
              :disabled="!username || !password || isLoading"
            >
              <span v-if="isLoading" class="loading-content">
                <span
                  class="spinner-border spinner-border-sm"
                  aria-hidden="true"
                />
                <output class="visually-hidden" aria-live="polite">
                  {{ $t("loggingIn") }}
                </output>
              </span>
              <span v-else>
                {{ $t("login") }}
              </span>
            </button>
            <div class="login-text">
              <p>{{ $t("agreeTerms") }}<a href="/userterms" target="_blank">{{ $t("userTerms") }}</a></p>
            </div>
          </form>
        </div>
      </div>
      <div class="col-lg-6 d-none d-lg-block">
        <img src="/images/BG_login.jpg" class="login-img" alt="">
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import config from '@/config.json'
import router from '../router'
import { isLoggedIn, userPermissions, setAuthState, currentUser} from '@/auth'
import { setAccessToken, setRefreshToken, setStoredPermissions, setStoredUsername } from '@/utils/token';

export default {
  name: 'HomeLogin',
  data() {
    return {
      username: '',
      password: '',
      errorMessage: '',
      isLoading: false,
      // 假帳號資料庫
      fakeUsers: [
        {
          username: 'testadmin',
          password: '', // NOSONAR - placeholder only
          name: '測試用超級使用者',
          enable_train: true,
          enable_inference: true,
          enable_annotation: true,
          enable_user_manage: true
        },
        {
          username: 'testuser',
          password: '', // NOSONAR - placeholder only
          name: '測試用一般使用者',
          enable_train: false,
          enable_inference: true,
          enable_annotation: true,
          enable_user_manage: false
        }
      ]
    }
  },
  // 新增：在組件載入時檢查登入狀態
  mounted() {
    this.checkLoginStatus()
  },
  methods: {
    // 新增：檢查登入狀態方法
    checkLoginStatus() {
      // 如果已經登入，根據權限跳轉到對應頁面
      if (isLoggedIn.value) {
        if (this.username == 'showTP' || currentUser.value == 'showTP' || this.username == 'onlyCanal' || currentUser.value == 'onlyCanal' || this.username == 'InfoTech2026' || currentUser.value == 'InfoTech2026' ) { 
          router.push({ name: 'TimePortrait' })
        } else if (userPermissions.value.enable_inference) {
          router.push({ name: 'ImageGeneration' })
        } else if (userPermissions.value.enable_annotation) {
          router.push({ name: 'ImageChecker' })
        } else if (userPermissions.value.enable_user_manage) {
          router.push({ name: 'UserManagement' })
        } else {
          // 如果沒有任何權限，保持在登入頁面但顯示錯誤訊息
          this.errorMessage = this.$t('noPermission')
        }
      }
    },

    async onSubmit() {
      if (!this.username || !this.password) return
      
      this.isLoading = true
      this.errorMessage = ''
      
      try {
        const res = await axios.post(
          `${config.apiBaseUrl}/api/annotations/token`,
          {
            username: this.username,
            password: this.password
          }
        )

        this.handleLoginSuccess(res.data)
      } catch (err) {
        if (err.response && err.response.status === 401) {
          this.errorMessage = this.$t('wrongNameOrPwd')
        } else {
          this.errorMessage = this.$t('loginFail')
        }
      } finally {
        this.isLoading = false
      }
    },
    
    // 處理登入成功
    handleLoginSuccess(userData) {
      setAccessToken(userData.access)
      setRefreshToken(userData.refresh)

      const permissions = {
        enable_inference: !!userData.enable_inference,
        enable_annotation: !!userData.enable_annotation,
        enable_user_manage: !!userData.enable_user_manage,
        enable_train: !!userData.enable_train
      }
      
      const username = userData.name || userData.username || this.username

      // 1) 存進 localStorage，給「新分頁 / F5」用
      setStoredPermissions(permissions)
      setStoredUsername(username)

      // 2) 設定到 Vue 全域狀態，讓目前頁面立刻更新 UI
      setAuthState({
        username, // 或後端回傳 userData.username
        permissions
      })

      // 3) 這裡再呼叫你的 checkLoginStatus(true) 決定要導去哪
      this.checkLoginStatus(true)
    }
  }
}
</script>
  
<style scoped>
/* 登入標題樣式 */
.login-wrapper {
  padding: 6rem 3rem 0;
}

.login-img {
  width: 100%;
  height: 100%;
  max-height: 80vh;
  border-radius: 30px;
}

.login-title {
  font-size: 45px;
  text-align: left;
  color: #333;
  margin-bottom: 5rem;
}

/* 新增：返回首頁按鈕樣式 */
.back-to-home-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #000;
  font-size: 36px;
  transition: all 0.2s ease;
  z-index: 10;
}

.back-to-home-btn:hover {
  color: #495057;
  text-decoration: none;
  transform: scale(1.05);
}

.back-to-home-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

/* 表單標籤樣式 */
.form-label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-size: 18px;
  font-weight: 500;
}

/* 自訂輸入框樣式 */
.custom-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 25px;
  font-size: 18px;
  background-color: white;
  transition: all 0.2s ease;
}

.custom-input:focus {
  outline: none;
  border-color: #333;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
}

.custom-input::placeholder {
  color: #999;
}

/* 自訂警告框樣式 */
.custom-alert {
  border-radius: 15px;
  border: none;
  padding: 12px 16px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

/* 登入按鈕樣式 */
.login-btn {
  display: block;
  width: 100%;
  margin: 0 auto;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.login-btn:disabled {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
  cursor: not-allowed;
  opacity: 0.6;
}

.login-text {
  padding: 1rem 0;
  text-align: center;
}

/* 新增：載入狀態垂直排列樣式 */
.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.loading-text {
  font-size: 0.9rem;
}

/* 響應式設計 */
@media (max-width: 992px) {
  .login-wrapper {
    padding: 8rem 6rem;
    box-shadow:  0 2px 10px rgba(0, 0, 0, 0.3);
  }
  .login-title {
    text-align: center;
    margin-bottom: 6rem;
  }
  
  .custom-input {
    padding: 10px 14px;
    font-size: 0.9rem;
  }
}

@media (max-width: 767px) {
  .login-wrapper {
    padding: 3rem 2rem;
  }
  
  .login-title {
    margin-bottom: 3rem;
    font-size: 28px;
    line-height: 40px;
    letter-spacing: 2px;
  }
  
  /* 手機版按鈕調整 */
  .back-to-home-btn {
    top: 15px;
    right: 15px;
    width: 35px;
    height: 35px;
    font-size: 24px;
  }
  
  /* 手機版載入文字稍微縮小 */
  .loading-text {
    font-size: 0.85rem;
  }
}
</style>
